/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TicketsList.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Divider,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CustomButtonV2 from "../../../../components/customComponentsV2/common/button/CustomButtonV2";
import { componentStyle } from "../../../../config/appTheme";

/**
 * Props:
 * - selectedStandard
 * - similarTickets
 * - disabledStandards
 * - onCreateTicket({ standardCode })
 * - onSelectTicket(ticket)
 * - createdTicketIds (optional) -> array of intr_id already created
 * - loading (optional)
 */
function TicketListDecisionMatrix({
  selectedStandard,
  similarTickets = [],
  disabledStandards = [],
  onCreateTicket,
  onSelectTicket,
  createdTicketIds = [],
  loading = false,
}) {
  const standardCode = selectedStandard?.standard_code;

  const isStandardDisabled = useMemo(
    () => disabledStandards.includes(standardCode),
    [disabledStandards, standardCode],
  );

  const handleCreateClick = () => {
    if (!standardCode || isStandardDisabled) return;

    onCreateTicket?.({
      standardCode,
    });
  };

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid #D0D0D0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography fontWeight={600}>
          Tickets for Legal Standard {selectedStandard?.standard_name || "-"}
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Ticket List */}
      <Box sx={{ flex: 1, overflow: "auto", ...componentStyle.scroll }}>
        <List dense>
          {similarTickets?.length > 0 ? (
            similarTickets.map((ticket) => {
              const isTicketCreated = createdTicketIds.includes(
                ticket?.intr_id,
              );

              return (
                <ListItemButton
                  key={ticket?.intr_id}
                  disabled={isTicketCreated}
                  onClick={() => !isTicketCreated && onSelectTicket?.(ticket)}
                  sx={{
                    mb: 1,
                    borderRadius: 0,
                    border: "1px solid #D0D0D0",
                    opacity: isTicketCreated ? 0.5 : 1,
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    {ticket?.legal_requirements?.map((lr, index) => (
                      <Box key={index} sx={{ mb: 1 }}>
                        <Typography fontWeight={500}>
                          Chapter : {lr?.chapter || "Ticket Details"}
                        </Typography>

                        <Typography variant="body2">
                          Requirement :{" "}
                          {lr?.description || "No description available."}
                        </Typography>
                      </Box>
                    ))}

                
                  </Box>

                  <IconButton size="small" disabled={isTicketCreated}>
                    <AutoAwesomeIcon
                      sx={{ height: "1rem", width: "1rem", color: "black" }}
                    />
                  </IconButton>
                </ListItemButton>
              );
            })
          ) : (
            <Typography variant="body2" sx={{ p: 2 }}>
              {loading
                ? "Loading similar tickets..."
                : "No similar tickets found."}
            </Typography>
          )}
        </List>
      </Box>

      {/* Footer Action */}
      {similarTickets?.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <CustomButtonV2
            variant="contained"
            size="small"
            btnName="Create Ticket"
            disabled={isStandardDisabled || loading}
            handleOnClick={handleCreateClick}
          />
        </Box>
      )}
    </Box>
  );
}

export default TicketListDecisionMatrix;
